/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import * as _ from 'lodash'
import { Collapse } from 'antd'
import useRootStore from '@store/useRootStore'
import PageLoading from '@components/PageLoading'
import { DetailMatchTrack, MarketTrack, MatchTrack, MatchupDetailFilter } from '@store/matchupStore'
import { GAME_TYPE } from '@game-type/game-market-types'
import { MatchWrapper } from '@styles/match.style'
import { isMobile } from 'react-device-detect'
import {
  getMatchPeriodBySport,
  validateBetLimitScore,
} from '@v2/utils/match-utils'
import { EventStatusType, ProducerType } from '@game-type/betradar-type'
import FavoriteImg from '@assets/images/btn/favorite-manage.png'
import FavoriteActiveImg from '@assets/images/btn/favorite-manage-active.png'
import { Button } from '@styles/button.style'
import { ToolTipIco } from '@shared/Icon/MatchupIcon'
import MarketTooltip from '../MarketTooltip'
import OutcomeBlockNew from '../OutcomeBlockNew'
import menus from '@views/PrimaryLayout/menu'
import { AdvEmpty } from '@components/AdvEmpty'
import { EMPTY_ICON, MATCHUP_DETAIL_STATIC_FILTER_INDEX } from '@constants/index'
import { observer } from 'mobx-react'
import { RoundTabWrap } from '@styles/base.style'
import { Tabs } from 'antd-mobile'
import { Models } from 'rmc-tabs/lib/Models'
import { PAGE_TYPE } from '@interface/common'
import { matchupDetailScrollToTop } from '@utils/utils'
import { GlobalStoreKey, PlayingPeriodSpecifierKeysByScore } from '@v2/constants/common'
import { PlayingPeriodSpecifierKey } from '@game/util'
import DialogUtils from '@utils/dialog-utils'

interface DetailMarketProps {
  isLoading: boolean
  setLoading: any
  detailTabWrapClassName: string
  iframeWrapTypeClassName: string
  visualSupport: boolean
  onChangeDetailTabWarpClassName: (className: string) => void
}

const ODDS_ACCORDION_KEY_PREFIX = 'odds_accordion_'
const DETAIL_BET_FAVORITES_CLASS = {
  ACTIVE: 'detail-bet-favorites-btn active',
  DISABLED: 'detail-bet-favorites-btn',
}
const DETAIL_INFO_CLASS = {
  SHOW: 'market-info--btn show',
  HIDE: 'market-info--btn',
  DISABLED: 'adv-hidden',
}

let intervalObj

export const DetailMarket: React.FC<DetailMarketProps> = observer((props: DetailMarketProps) => {
  const [defaultAccordionActiveKeys, setDefaultAccordionActiveKeys] = useState<string[]>([])
  const [accordionActiveKey, setAccordionActiveKey] = useState<string[]>([])
  const [currentFilterIndex, setCurrentFilterIndex] = useState(0)
  const [curMatchId, setCurrentMatchId] = useState(0)
  const [filters, setFilters] = useState([])
  const refTooltipButtons = useRef([])

  const {
    matchupStore,
    routerStore,
    globalStore,
    authStore,
    marketTooltipStore,
  } = useRootStore()

  const {
    isLoading,
    setLoading,
    detailTabWrapClassName,
    iframeWrapTypeClassName,
    visualSupport,
    onChangeDetailTabWarpClassName
  } = props

  const {
    club = {},
    pageType,
    isKeepMatchDetail,
    currentGameKey,
    favoriteMarkets,
    producerId,
    detailTrack,
  } = matchupStore

  const { id: clubId } = club

  const {
    gameKey,
    sportId = 178,
    tournamentId,
  } = detailTrack || ({} as DetailMatchTrack)

  const track = detailTrack?.track ?? ({} as MatchTrack)
  const {
    matchId,
    irregulars = [],
    homeTeamName = '홈팀',
    awayTeamName = '원정팀',
    statusCode,
  } = track

  let filtered: MarketTrack[] = []

  useEffect((): void => {
    marketTooltipStore.cleanup()
    setFilters([])
    initialize().catch(console.error)
  }, [matchId])

  useEffect((): void => {
    if (authStore.isSignedIn) {
      matchupStore.updateFavoriteMarkets(sportId)
    }
  }, [sportId, currentGameKey])

  useEffect((): () => void => {
    // 10초 마다 마켓들을 갱신 해준다.
    if (producerId === ProducerType.LIVEODDS && !_.isNil(matchId)) {
      if (intervalObj) {
        clearInterval(intervalObj)
      }
      intervalObj = setInterval(() => {
        fetchMarkets(true).catch(console.error)
      }, 10000)
    } else if (intervalObj) {
      clearInterval(intervalObj)
    }
    return (): void => {
      if (intervalObj) {
        clearInterval(intervalObj)
      }
    }
  }, [producerId, matchId])

  useEffect(() => {
    marketTooltipStore.updateMarketTooltips(filtered)
  }, [filtered])

  const initialize = async (): Promise<void> => {
    if (_.isEmpty(detailTrack) && !isKeepMatchDetail) {
      if (_.isNil(producerId) || _.isNil(matchId)) {
        goBack()
        return
      }

      if (pageType === PAGE_TYPE.MATCHUP_RESULT) {
        goBack()
        return
      }

      if (_.isNil(clubId)) {
        goBack()
        return
      }

      if (_.isEmpty(club)) {
        await matchupStore.fetchMatchupClub({ clubId })
      }

      matchupStore.updateDescriptor()

      await fetchMarkets()
    }


    if (pageType !== PAGE_TYPE.MATCHUP_RESULT && detailTrack) {
      await fetchFilters(detailTrack.sportId)
    }
  }

  const fetchMarkets = async (isLive = false): Promise<void> => {
    await matchupStore.fetchMatchupDetailMarkets(
      { gameType: matchupStore.gameKey, clubId, producerId, matchId, tournamentId },
      isLive,
    )
  }

  const fetchFilters = async (filterSportId: number) => {
    if (filterSportId <= 0 || (
      filters?.length > 0 && matchId === curMatchId
    )) {
      return
    }

    setCurrentMatchId(matchId)
    if (!detailTrack) {
      setLoading(true)
    }
    const ret = await matchupStore.fetchMatchupDetailFilters(filterSportId)
    if (_.isArray(ret)) {
      const tmp = _.filter(ret, o => {
        if (gameKey === GAME_TYPE.LIVE) {
          return o.id >= 20000
        }
        return o.id < 20000
      })
      tmp.sort((a, b) => {
        return a.categoryId - b.categoryId
      })
      setFilters(tmp)

      // 시작 인덱스 설정
      const { matchupDetailTabInitialIndex = MATCHUP_DETAIL_STATIC_FILTER_INDEX.TOTAL } = globalStore
      const startIndex = matchupDetailTabInitialIndex

      setCurrentFilterIndex(startIndex)
    } else {
      setFilters([])
    }
    setLoading(false)
  }

  const goBack = (): void => {
    if (!isMobile) {
      if (intervalObj) {
        clearInterval(intervalObj)
      }

      matchupStore.clearDetailTrack()
      return
    }

    globalStore.setVisibleMatchupDetailPopup(false)
    globalStore.closeGeneralPopup()
  }

  const makeOddsAccordionKey = market => `${ODDS_ACCORDION_KEY_PREFIX}${market.name}${market.id}`
  const makeDefaultAccordionActiveKeys = () => {
    const newKeys = []
    _.forEach(irregulars, market => {
      const key = makeOddsAccordionKey(market)
      if (market.defaultFolds && !market.defaultFolds[sportId]) {
        newKeys.push(key)
      }
    })
    return newKeys
  }

  const onFilterTabChange = (td: Models.TabData, idx: number) => {
    setCurrentFilterIndex(idx)

    const matchDetailWrap = document.getElementById('matchDetailWrap')
    if (!matchDetailWrap) return

    const isMatchupResult = pageType === PAGE_TYPE.MATCHUP_RESULT || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER
    const isInProgress = statusCode === EventStatusType.Live
    const showVisualSupport = visualSupport && (currentGameKey === GAME_TYPE.LIVE || (isMatchupResult && isInProgress))
    const targetId = showVisualSupport ? 'betIframeWrap' : 'gamePreviewWrap'
    const targetWrap = document.getElementById(targetId)
    if (!targetWrap) return

    const targetHeight = targetWrap.clientHeight
    const isLive = (currentGameKey === GAME_TYPE.LIVE || (isMatchupResult && isInProgress)) && document.getElementById('betIframeWrap')


    const isShowSimpleInfo = isMobile && ['show-simple-info', 'live-score-tab'].includes(detailTabWrapClassName)
    const isPcScrolled = !isMobile && matchDetailWrap.scrollTop > targetHeight

    if (isShowSimpleInfo || isPcScrolled) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          matchDetailWrap.scrollTo({
            top: targetWrap.clientHeight + (isMobile ? 11 : 1) + (isLive ? 26 : 0),
            behavior: 'instant',
          })
        })
      })
      if (isMobile) {
        onChangeDetailTabWarpClassName(showVisualSupport ? 'live-score-tab' : 'show-simple-info')
      }
    } else {
      console.log('detailMarket to top')
      matchupDetailScrollToTop()
    }
  }

  /**
   * 마켓 별 아코디언 펼침 여부 기본 값으로 현재 아코디언 펼침 상태를 갱신한다.
   * 화면에 새로 추가된 마켓인 경우 마켓의 기본 값을 기준으로 활성 여부를 판단한다.
   * @param {string[]} newDefaultAccordionActiveKeys - 현재 경기 - 마켓 리스트의 펼침 상태 기본값으로 생성된 펼침 상태 키 목록
   * @return {void}
   */
  const updateAccordionActiveKeys = (newDefaultAccordionActiveKeys: string[]): void => {
    _.forEach(newDefaultAccordionActiveKeys, key => {
      // 마켓의 기본 펼침 상태 키 목록에 없는 key가 있다면 새로운 마켓이 표시되는 경우임.
      // 새로운 마켓이 표시되었고, 펼침 상태 키 목록에 있는 경우 아코디언은 펼침 상태가 되어야 한다.
      if (!_.includes(defaultAccordionActiveKeys, key)) {
        accordionActiveKey.push(key)
        setAccordionActiveKey(accordionActiveKey)
      }
    })
  }

  if (detailTrack && _.size(irregulars) > 0) {
    const newDefaultAccordionActiveKeys = makeDefaultAccordionActiveKeys()
    if (!_.isEqual(newDefaultAccordionActiveKeys, defaultAccordionActiveKeys)) {
      updateAccordionActiveKeys(newDefaultAccordionActiveKeys)
      setDefaultAccordionActiveKeys(newDefaultAccordionActiveKeys)
    }
  }

  const setActiveTooltip = (classList: DOMTokenList, market) => {
    if (_.includes(classList, 'show')) {
      classList.remove('show')
      marketTooltipStore.setActiveTooltip(market, false)
    } else {
      classList.add('show')
      marketTooltipStore.setActiveTooltip(market, true)
    }
  }

  const onChangeCollapse = (newKeys) => {
    setAccordionActiveKey(newKeys)

    const key = marketTooltipStore.getExcludeKey(newKeys)
    if (key !== '') {
      const ref = refTooltipButtons.current[key]
      const marketName = key.replace(ODDS_ACCORDION_KEY_PREFIX, '')
      const market = irregulars.find(e => e.name === marketName)
      if (ref && _.includes(ref.classList, 'show')
        && market.descActive === true) {
        setActiveTooltip(ref.classList, market)
      }
    }
  }

  const onClickDetailMarketFavorites = async (e, market) => {
    e.stopPropagation()
    let currentScrollTop = 0
    const matchDetailWrap = document.getElementById('matchDetailWrap')
    if (matchDetailWrap) {
      currentScrollTop = document.getElementById('matchDetailWrap').scrollTop
    }

    if (!authStore.isSignedIn) {
      DialogUtils.showGuestMode()
      return
    }

    const periodValues = _.chain(market.playingPeriodInfos).map(info => info.value).join(',').value()
    await matchupStore.toggleFavoriteMarket(sportId, market.type, periodValues)

    if (matchDetailWrap) {
      matchDetailWrap.scrollTop = currentScrollTop
    }
  }

  let availableIrregulars: MarketTrack[] = irregulars

  if (currentGameKey === GAME_TYPE.LIVE) {
    const previousPeriodFilter = globalStore.findGlobalSetting(GlobalStoreKey.LIVE_PREVIOUS_PERIOD_MARKET_FILTER_ENABLE).value === '1'

    if (previousPeriodFilter) {
      availableIrregulars = _.filter(availableIrregulars, (o: MarketTrack) => {
        const { matchStatusCode, periodScores } = detailTrack.track
        const { playingPeriodInfos } = o

        if (_.isEmpty(playingPeriodInfos)) {
          return true
        }

        const currentPeriod = getMatchPeriodBySport(sportId, matchStatusCode, periodScores)
        const playPeriodInfo = _.find(playingPeriodInfos, periodInfo => {
          const { key } = periodInfo
          return !_.some(PlayingPeriodSpecifierKeysByScore, item => _.includes(key, `${item}`))
            && _.includes(PlayingPeriodSpecifierKey, key)
        })

        const marketPeriod = playPeriodInfo?.value || '0'
        return _.toNumber(marketPeriod) >= currentPeriod
      })
    }
  }

  const isVisibleSpecialTab = availableIrregulars.filter(market => market.isSpecial).length > 0

  const filterTabs = []
  if (!isLoading && pageType !== PAGE_TYPE.MATCHUP_RESULT) {
    filterTabs.push({
      title: (
        <div className="round-tab-wrap">
          <span className="txt">전체</span>
          <span className="num">{availableIrregulars.length}</span>
        </div>
      ),
      key: MATCHUP_DETAIL_STATIC_FILTER_INDEX.TOTAL,
    })

    if (isVisibleSpecialTab) {
      filterTabs.push({
        title: (
          <div className="round-tab-wrap">
            <span className="txt">스페셜</span>
            <span className="num">{availableIrregulars.filter(market => market.isSpecial).length}</span>
          </div>
        ),
        key: MATCHUP_DETAIL_STATIC_FILTER_INDEX.SPECIAL,
      })
    }
    for (const el of filters as MatchupDetailFilter[]) {
      if (!el.id) {
        continue
      }
      const tmp = _.filter(availableIrregulars, (o: MarketTrack) => {
        return el.marketIds?.indexOf(Number(o.type)) >= 0
      })

      if (_.size(tmp) === 0 && currentGameKey === GAME_TYPE.CROSS) {
        continue
      }

      filterTabs.push({
        title: (
          <div className="round-tab-wrap">
            <span className="txt">{el.name}</span>
            <span className="num">{tmp?.length}</span>
          </div>
        ),
        key: el.id.toString(),
      })
    }
  }

  if (currentFilterIndex === MATCHUP_DETAIL_STATIC_FILTER_INDEX.TOTAL) {
    filtered = availableIrregulars
  } else if (isVisibleSpecialTab && currentFilterIndex === MATCHUP_DETAIL_STATIC_FILTER_INDEX.SPECIAL) {
    filtered = availableIrregulars.filter(market => market.isSpecial)
  } else if (!_.isNil(filterTabs[currentFilterIndex]?.key)) {
    const filterId = _.toNumber(filterTabs[currentFilterIndex]?.key)
    const found = _.find(filters, { id: filterId })

    if (found) {
      filtered = _.filter(availableIrregulars, (o: MarketTrack) => {
        return found.marketIds.indexOf(Number(o.type)) >= 0
      })
    }
  }

  // 유저별 마켓찜 정렬
  if (favoriteMarkets && favoriteMarkets.sportId === sportId && pageType !== PAGE_TYPE.MATCHUP_RESULT) {
    const favoriteFiltered = []
    const otherFiltered = []

    for (const filter of filtered) {
      const periodValues = _.chain(filter.playingPeriodInfos).map(info => info.value).join(',').value()
      const isFavorite = matchupStore.isFavoriteMarket(filter.type, periodValues)
      if (isFavorite) {
        favoriteFiltered.push(filter)
      } else {
        otherFiltered.push(filter)
      }
    }

    filtered = favoriteFiltered.concat(otherFiltered)
  }

  const makeHeader = (market, oddsAccordionKey, isFavoriteMarket: boolean) => {
    let favoritesClassName = DETAIL_BET_FAVORITES_CLASS.DISABLED
    if (authStore.isSignedIn && favoriteMarkets) {
      if (isFavoriteMarket) {
        favoritesClassName = DETAIL_BET_FAVORITES_CLASS.ACTIVE
      }
    }

    const onClickFavorites = async (e) => {
      await onClickDetailMarketFavorites(e, market)
    }

    let marketName = (
      <Fragment>
        {producerId !== ProducerType.LIVEODDS && matchupStore.isMainMarket(market.type) &&
          <img src={`${globalStore.getIngameImagesUrl()}icon/main_market_icon.png`} className="main-market-icon"
               alt="메인 아이콘"/>
        }
        <span className="market-info--name">{market.name}</span>
      </Fragment>
    )
    if (market.teamName) {
      const tmpName = market.name.split(market.teamName)
      const teamName = (<span className="market-info--team-name">{market.teamName}</span>)
      marketName = (
        <span className="market-info--name">
          {tmpName[0]}
          {teamName}
          {tmpName[1]}
        </span>
      )
    }

    const onClickOpenMarketInfo = (e) => {
      const selectedKey = makeOddsAccordionKey(market)
      marketTooltipStore.pushSelectedActiveKey(selectedKey)
      try {
        if (e.currentTarget.parentElement.parentElement.parentElement.attributes[3].nodeValue === 'true') {
          e.stopPropagation()
        }
      } catch (err) {
        console.error('onClickOpenMarketInfo err', err)
      }

      setActiveTooltip(e.currentTarget.classList, market)
    }

    const FavoriteBtn = <img src={FavoriteImg} className="favorite-btn-img" alt="favorite-btn"/>
    const FavoriteActiveBtn = <img src={FavoriteActiveImg} className="favorite-btn-active-img"
                                   alt="favorite-btn-active"/>

    return (
      <div className="market-header">
        <div className="market-info">
          {market.descActive === true &&
            <Button ref={el => {
              refTooltipButtons.current[oddsAccordionKey] = el
            }} className={DETAIL_INFO_CLASS.HIDE} onClick={onClickOpenMarketInfo}>
              <ToolTipIco/>
            </Button>
          }
          {marketName}
        </div>

        <Button TP className={favoritesClassName} onClick={onClickFavorites}>
          <span className="btn-bg"/>
          {/* 찜버튼 - 추가bet*/}
          {FavoriteBtn}
          {FavoriteActiveBtn}
        </Button>
      </div>
    )
  }

  const renderFilterTabs = () => {
    return (
      <RoundTabWrap
        className={`${detailTabWrapClassName} ${iframeWrapTypeClassName} matchup-detail matcup-filter`}>
        <Tabs
          tabs={filterTabs}
          page={currentFilterIndex}
          prefixCls="detail-tab am-tabs"
          onChange={onFilterTabChange}
          renderTabBar={tabProps => {
            return (
              <Tabs.DefaultTabBar {...tabProps} page={3.6}/>
            )
          }}
        />
      </RoundTabWrap>
    )
  }

  const renderDetailMarkets = () => {
    const outcomeBlockBaseProps = {
      sportId,
      homeTeamName,
      awayTeamName,
      tournamentId,
      matchId,
      disableMatchScore: false,
      minimumLiveMatchOutcomeRate: globalStore.getMinimumLiveMatchOutcomeRate(),
    }

    const {
      homeScore,
      awayScore,
      periodScores,
      matchStatusCode,
    } = track

    let betLimitScoreInfo
    if (currentGameKey === GAME_TYPE.LIVE) {
      betLimitScoreInfo = globalStore.getBetLimitScoreInfoBySportId(sportId)
    }

    return (
      <MatchWrapper className={isMobile ? 'match-detail accordion-wrap' : 'match-detail accordion-wrap pc'}>
        <Collapse
          defaultActiveKey={defaultAccordionActiveKeys}
          activeKey={accordionActiveKey}
          onChange={onChangeCollapse}>
          {filtered.map((market) => {
            if (betLimitScoreInfo) {
              const marketType = _.toNumber(market.type)
              const disableMatchScore = !validateBetLimitScore({
                marketType,
                matchStatusCode,
                homeScore,
                awayScore,
                periodScores,
                betLimitScoreInfo,
              })

              _.assign(outcomeBlockBaseProps, { disableMatchScore })
            }

            const oddsAccordionKey = makeOddsAccordionKey(market)
            const periodValues = _.chain(market.playingPeriodInfos)
              .map(info => info.value).join(',').value()
            const isFavoriteMarket = matchupStore.isFavoriteMarket(market.type, periodValues)

            let isMainMarketStyle = false
            if (producerId !== ProducerType.LIVEODDS) {
              isMainMarketStyle = matchupStore.isMainMarket(market.type)
            }

            return (_.isNumber(market.renderingType) && market.renderingType > 0 &&
              <Collapse.Panel key={oddsAccordionKey}
                              header={makeHeader(market, oddsAccordionKey, isFavoriteMarket)}
                              css={{ position: 'relative' }} showArrow={false}
                              className={isMainMarketStyle && 'main-market'}>
                <MarketTooltip market={market}/>
                <OutcomeBlockNew {...outcomeBlockBaseProps} marketTrack={market}/>
              </Collapse.Panel>
            )
          })}
        </Collapse>
      </MatchWrapper>
    )
  }

  const renderEmptyDetailMarkets = () => {
    let ico = EMPTY_ICON.MATCH_DETAIL
    if (!isMobile) {
      const pathName = routerStore.location.pathname
      const menu = _.find(menus, { namespace: 'matchupResult' })
      if (pathName === menu.path) {
        ico = EMPTY_ICON.MATCH_RESULT_PC
      } else {
        ico = EMPTY_ICON.MATCH_DETAIL_PC
      }
    }
    return (
      <AdvEmpty ico={ico}/>
    )
  }

  const render = () => {
    let footer
    if (isLoading) {
      footer = <PageLoading/>
    } else {
      // ********************* 베팅정보(BET) *********************
      if (detailTrack) {
        footer = (
          <Fragment>
            { renderFilterTabs() }
            { filtered.length > 0 ? renderDetailMarkets() : renderEmptyDetailMarkets() }
          </Fragment>
        )
      } else {
        footer = renderEmptyDetailMarkets()
      }
    }
    return footer
  }

  return render()
})
